import { useState } from "react";
import { createPortal } from "react-dom";
import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";
import { useT } from "../../i18n/LanguageProvider";
import { errorText } from "../../i18n/serverErrors";
import {
  PRINTER_ROLES,
  forgetPrinter,
  pairPrinter,
  printerTail,
  readRegistry,
  roleLabel,
  type PrinterRegistry,
  type PrinterRole,
} from "../../platform/printerRegistry";
import { chipState, sharesMachine } from "./printerChipState";
import { ShopFloorIcon } from "./ShopFloorIcon";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-155 — the two printers on this counter, managed from the kiosk
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Owner, 2026-10-14: *"florists and everyone should be able to repair the
 * printer from kiosk mode in easy way, both printers sticker and ticket …
 * both printers will have button, each showing paired printers name, if not
 * paired show button 'Pair'. already paired ones can be forgotten/unpaired
 * via clicking on them."*
 *
 * Before this, forgetting a printer lived only on the office Receipts page,
 * which a florist or a manager cannot reach inside the tablet app (OD-35
 * confines them to the kiosk). A wrong pairing — the other "X5" on the shelf —
 * therefore needed an owner at the tablet. These two chips sit in the header
 * of "Print a note", the one kiosk screen every florist opens that already
 * talks about the printer.
 *
 * TWO CHIPS, THREE STATES EACH (ruled on the mockup, OD-249):
 *   - paired   → the printer's name and the tail of its address; tap opens a
 *                small card with "Pair a different printer" and "Forget";
 *   - borrowed → "Same as ticket printer" / "Same as sticker printer": the
 *                role is empty and the other role's machine covers it (the
 *                normal one-printer counter, see `FALLS_BACK_TO`); tap pairs
 *                a separate machine for this role;
 *   - empty    → "Pair"; tap opens Android's device picker for this role.
 *
 * FORGET IS TWO TAPS, NOT ONE. The agent's pushback, taken: a florist who taps
 * a chip to CHECK which printer it is must not lose the pairing and have to
 * run the Bluetooth picker again on a busy counter. So the chip opens a card,
 * and "Forget this printer" is a button on it. Still zero typing.
 *
 * THE REGISTRY IS READ WHEN THE SCREEN OPENS and again after this component's
 * own writes, never held across screens: it is a
 * `localStorage` read, and a printer paired on the box label or the item
 * sticker shows here the next time "Print a note" is opened. Not on every
 * render — the note panel re-renders on every keystroke and the header with
 * it, and parsing the registry 400 times for a 400-character note is waste.
 * The chip RULES (`chipState`, `sharesMachine`) live in `printerChipState.ts`,
 * pure and tested without React.
 */

const CHIP_ICON: Record<PrinterRole, "print" | "notes"> = {
  receipts: "print",
  notes: "notes",
};

export function ShopFloorPrinterChips({
  disabled = false,
  onChanged,
}: {
  disabled?: boolean;
  /** Fired after a pairing or a forget, so the screen re-resolves its printer. */
  onChanged: () => void;
}) {
  const { t } = useT();
  const [registry, setRegistry] = useState<PrinterRegistry>(() => readRegistry());
  const [pairing, setPairing] = useState<PrinterRole | null>(null);
  const [open, setOpen] = useState<PrinterRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  const changed = () => {
    setRegistry(readRegistry());
    onChanged();
  };

  const pair = async (role: PrinterRole) => {
    setPairing(role);
    setError(null);
    try {
      await pairPrinter(role);
    } catch (err: unknown) {
      setError(errorText(err, "printer.couldNotPair"));
      return;
    } finally {
      setPairing(null);
    }
    // Outside the try, as in `PairPrinterPrompt`: a parent re-render that
    // throws must not be reported as a pairing failure after the registry was
    // already written.
    setOpen(null);
    changed();
  };

  const forget = (role: PrinterRole) => {
    forgetPrinter(role);
    setOpen(null);
    setError(null);
    changed();
  };

  return (
    <>
      <div className="sf-printer-chips">
        {PRINTER_ROLES.map((role) => {
          const state = chipState(registry, role);
          const busy = pairing === role;
          const className =
            state.kind === "paired"
              ? "sf-printer-chip is-paired"
              : state.kind === "borrowed"
                ? "sf-printer-chip is-borrowed"
                : "sf-printer-chip is-unpaired";
          return (
            <button
              key={role}
              type="button"
              className={className}
              disabled={disabled || pairing !== null}
              onClick={() =>
                state.kind === "paired" ? setOpen(role) : void pair(role)
              }
            >
              <span className="sf-printer-chip-icon" aria-hidden="true">
                <ShopFloorIcon name={CHIP_ICON[role]} size={22} />
              </span>
              <span className="sf-printer-chip-text">
                <span className="sf-printer-chip-role">{roleLabel(role)}</span>
                <span className="sf-printer-chip-value">
                  {busy
                    ? t("printer.waiting")
                    : state.kind === "paired"
                      ? <>
                          {state.printer.printer.name ?? t("note.thePrinter")}{" "}
                          <span className="sf-printer-chip-tail">
                            …{printerTail(state.printer.printer.deviceId)}
                          </span>
                        </>
                      : state.kind === "borrowed"
                        ? t(
                            role === "notes"
                              ? "printerChips.sameAsTicket"
                              : "printerChips.sameAsSticker",
                          )
                        : t("printerChips.pair")}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {open && registry[open] ? (
        <PrinterChipCard
          role={open}
          registry={registry}
          busy={pairing !== null}
          onPairDifferent={() => void pair(open)}
          onForget={() => forget(open)}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

/** The card behind a paired chip: who this is, and the two things you can do. */
function PrinterChipCard({
  role,
  registry,
  busy,
  onPairDifferent,
  onForget,
  onClose,
}: {
  role: PrinterRole;
  registry: PrinterRegistry;
  busy: boolean;
  onPairDifferent: () => void;
  onForget: () => void;
  onClose: () => void;
}) {
  const { t } = useT();
  useDismissOnEscape(onClose);
  const printer = registry[role]!;
  const name = printer.name ?? t("note.thePrinter");
  const shared = sharesMachine(registry, role);

  return createPortal(
    <div
      className="sf-bouquet-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t("printerChips.cardAria", { role: roleLabel(role), printer: name })}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sf-bouquet-modal sf-printer-card" onClick={(e) => e.stopPropagation()}>
        <div className="sf-printer-card-head">
          <span className="sf-printer-card-glyph" aria-hidden="true">
            <ShopFloorIcon name={CHIP_ICON[role]} size={26} />
          </span>
          <span className="sf-printer-chip-text">
            <span className="sf-printer-chip-role">{roleLabel(role)}</span>
            <span className="sf-printer-card-name">
              {name}{" "}
              <span className="sf-printer-chip-tail">…{printerTail(printer.deviceId)}</span>
            </span>
          </span>
        </div>
        {shared ? (
          <p className="muted sf-printer-card-note">
            {t(role === "receipts" ? "printerChips.alsoStickers" : "printerChips.alsoTickets")}
          </p>
        ) : null}
        <div className="sf-printer-card-actions">
          <button type="button" className="button" onClick={onPairDifferent} disabled={busy}>
            {busy ? t("printer.waiting") : t("printerChips.pairDifferent")}
          </button>
          <button
            type="button"
            className="button sf-printer-card-forget"
            onClick={onForget}
            disabled={busy}
          >
            {t("printerChips.forget")}
          </button>
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            {t("printerChips.back")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
